import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiAlertCircle, FiChevronDown, FiLock } from 'react-icons/fi';
import { usePatientDoctorContext } from '../PatientDoctorContext';
import { allows, rightsOf, unitsOf, useSessionUser } from '../../lib/permissions';
import { toInputDate } from '../../lib/format';
import { withTimeout } from '../../lib/firebase';
import { BUTTON, CARD, EmptyState, Field, MultiChips, Notice, PageHeader, Panel, Segmented, Select, SIZE, SkeletonRows, TextArea, TextInput } from '../ui/kit';
import { FORM_FIELDS, SECTIONS, UNITS, birthDateOf, hasValue, splitList } from './patientModel';
import { createPatient, errorMessage, fetchPatient, patientAccess, updatePatient } from './api';
import AttachmentsField, { useDocumentsStatus } from '../documents/AttachmentsField';
import { saveFile, today, uploadFiles } from '../fichiers/fileModel';

const NEW_DEFAULTS = { nationalite: 'Sénégal', urgence: 'non', validation: 'Validé' };

const toFormValues = (patient = {}) => {
  const values = {};
  FORM_FIELDS.forEach((field) => {
    const raw = field.name === 'dateNaissance' ? birthDateOf(patient) : patient[field.name];
    if (field.type === 'multi') values[field.name] = splitList(raw);
    else if (field.type === 'date') values[field.name] = toInputDate(raw);
    else values[field.name] = raw === undefined || raw === null ? '' : String(raw);
  });
  return values;
};

const toPayload = (values) => {
  const payload = {};
  FORM_FIELDS.forEach((field) => {
    const value = values[field.name];
    if (field.type === 'multi') payload[field.name] = field.list === 'array' ? value : value.join(field.list);
    else payload[field.name] = typeof value === 'string' ? value.trim() : value;
  });
  // Older screens read the birth date from `age`: keep both in step.
  if (payload.dateNaissance) payload.age = payload.dateNaissance;
  return payload;
};

const bmi = (weight, height) => {
  const kg = parseFloat(String(weight).replace(',', '.'));
  const metres = parseFloat(String(height).replace(',', '.')) / 100;
  return kg > 0 && metres > 0 ? (kg / (metres * metres)).toFixed(1) : '';
};

// A <select> must list the value already saved, even if it is no longer offered.
const withCurrent = (field, value) => {
  if (!value || field.type !== 'select') return field;
  const flat = (field.groups ? field.groups.flatMap((group) => group.options) : field.options || []).map((option) =>
    typeof option === 'string' ? option : option.value
  );
  if (flat.includes(value)) return field;
  const current = { value, label: `${value} (valeur enregistrée)` };
  return field.groups
    ? { ...field, groups: [{ label: 'Valeur enregistrée', options: [current] }, ...field.groups] }
    : { ...field, options: [current, ...(field.options || [])] };
};

const PatientForm = () => {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const user = useSessionUser();
  const { patients, updatePatients, ensurePatients } = usePatientDoctorContext();
  const cached = editing ? patients.find((item) => item._id === id) : null;

  const [patient, setPatient] = useState(cached || null);
  const [status, setStatus] = useState(editing && !cached ? 'loading' : 'ready');
  const [values, setValues] = useState(() => (editing ? toFormValues(cached || {}) : { ...toFormValues({}), ...NEW_DEFAULTS }));
  const [unit, setUnit] = useState('');
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clinicalOpen, setClinicalOpen] = useState(false);
  // Documents joined to a new file (saved in "fichiers" once the patient exists).
  const [attached, setAttached] = useState({ ordre: '', links: [], pending: [] });
  const [sending, setSending] = useState(null); // { index, ratio } while the documents are sent
  const documentsReady = useDocumentsStatus();
  const imcAuto = useRef(true);

  useEffect(() => {
    if (!editing) {
      ensurePatients().catch(() => {});
      return undefined;
    }
    let cancelled = false;
    fetchPatient(id)
      .then((data) => {
        if (cancelled) return;
        setPatient(data);
        if (!dirty) setValues(toFormValues(data));
        setStatus('ready');
      })
      .catch(() => !cancelled && setStatus(cached ? 'ready' : 'error'));
    return () => {
      cancelled = true;
    };
    // Loaded once per file; later edits stay in the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!editing || !patient) return;
    const clinicalFilled = SECTIONS.filter((section) => section.clinical).some((section) =>
      section.fields.some((field) => hasValue(patient[field.name]))
    );
    setClinicalOpen(clinicalFilled);
    imcAuto.current = !patient.imc;
  }, [editing, patient]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const access = patientAccess(user, editing ? patient : null);
  // A new file always belongs to one of the account's units.
  const canAttach = !editing && allows(rightsOf(user), 'fichiers', 'modifier');
  const myUnits = useMemo(() => {
    if (user.role === 'Admin') return UNITS;
    const mine = unitsOf(user.service);
    return UNITS.filter((name) => mine.includes(name.toLowerCase()));
  }, [user.role, user.service]);

  const setValue = (name, value) => {
    setDirty(true);
    setErrors((current) => ({ ...current, [name]: '' }));
    setValues((current) => {
      const next = { ...current, [name]: value };
      if (name === 'imc') imcAuto.current = !value;
      if ((name === 'poids' || name === 'taille') && imcAuto.current) next.imc = bmi(next.poids, next.taille);
      return next;
    });
  };

  const validate = () => {
    const found = {};
    if (!values.nom.trim()) found.nom = 'Le nom du patient est obligatoire.';
    const dossier = values.dossierNumber.trim();
    if (!dossier) found.dossierNumber = 'Le numéro de dossier est obligatoire.';
    else {
      const owner = patients.find((item) => item.dossierNumber === dossier && item._id !== id);
      if (owner) found.dossierNumber = `Ce numéro est déjà utilisé pour ${String(owner.nom || '').toUpperCase()}.`;
    }
    return found;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    const found = validate();
    setErrors(found);
    setFormError('');
    if (Object.keys(found).length) {
      const first = document.getElementById(`field-${Object.keys(found)[0]}`);
      if (first) first.focus();
      return;
    }
    setSaving(true);
    try {
      const payload = toPayload(values);
      if (!editing && unit) payload.services = unit;
      const saved = editing ? await updatePatient(id, payload) : await createPatient(payload);
      updatePatients(editing ? patients.map((item) => (item._id === saved._id ? saved : item)) : [...patients, saved]);
      setDirty(false);
      let filesFailed = false;
      if (canAttach && (attached.links.length || attached.pending.length)) {
        let documents = [];
        try {
          documents = await uploadFiles(attached.pending, saved, setSending);
        } catch (error) {
          documents = error.sent || [];
          filesFailed = true;
        }
        setSending(null);
        if (documents.length || attached.links.length) {
          try {
            // Offline, the write waits in the queue and already shows in the file: do not hold the screen.
            await withTimeout(saveFile(user, saved, { ordre: attached.ordre, links: attached.links, documents, datePatient: today() }), 15000);
          } catch (error) {
            filesFailed = true;
          }
        }
      }
      navigate(`/patients/${saved._id}`, { replace: true, state: { saved: true, filesFailed } });
    } catch (error) {
      const data = (error.response && error.response.data) || {};
      if (data.code === 'DOSSIER_TAKEN') {
        setErrors({ dossierNumber: data.message });
        const field = document.getElementById('field-dossierNumber');
        if (field) field.focus();
      } else {
        setFormError(errorMessage(error, 'Le dossier n’a pas pu être enregistré. Vérifiez la connexion, puis réessayez.'));
      }
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    if (dirty && !window.confirm('Abandonner les modifications non enregistrées ?')) return;
    setDirty(false);
    navigate(editing ? `/patients/${id}` : '/patients');
  };

  const back = editing ? { to: `/patients/${id}`, label: 'Retour au dossier' } : { to: '/patients', label: 'Dossiers patients' };
  const shell = (content) => <main className="mx-auto max-w-5xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">{content}</main>;

  if (status === 'loading') {
    return shell(
      <>
        <PageHeader back={back} title="Chargement du dossier…" />
        <div className={`${CARD} mt-8 p-6`}>
          <SkeletonRows rows={4} />
        </div>
      </>
    );
  }
  if (status === 'error' || (editing && !patient)) {
    return shell(
      <>
        <PageHeader back={{ to: '/patients', label: 'Dossiers patients' }} title="Dossier introuvable" />
        <div className={`${CARD} mt-8`}>
          <EmptyState icon={FiAlertCircle} title="Le dossier n’a pas pu être chargé." />
        </div>
      </>
    );
  }
  if (editing ? !access.canEdit : !access.canCreate) {
    return shell(
      <>
        <PageHeader back={back} title={editing ? 'Modification impossible' : 'Création impossible'} />
        <div className={`${CARD} mt-8`}>
          <EmptyState icon={FiLock} title={editing && access.otherUnit ? `Ce dossier appartient à l’unité ${patient.services}.` : 'Vos droits ne permettent pas cette action.'}>
            {editing && access.otherUnit
              ? 'Seule cette unité peut le modifier. Vous pouvez y ajouter des mises à jour depuis le dossier.'
              : 'Demandez à l’administrateur si vous en avez besoin.'}
          </EmptyState>
        </div>
      </>
    );
  }

  const renderField = (rawField) => {
    const field = withCurrent(rawField, values[rawField.name]);
    if (field.showIf && !field.showIf(values)) return null;
    const fieldId = `field-${field.name}`;
    const value = values[field.name];
    let input;
    if (field.type === 'textarea') {
      input = <TextArea id={fieldId} value={value} onChange={(e) => setValue(field.name, e.target.value)} placeholder={field.placeholder} />;
    } else if (field.type === 'select') {
      input = <Select id={fieldId} value={value} onChange={(e) => setValue(field.name, e.target.value)} options={field.options} groups={field.groups} />;
    } else if (field.type === 'segmented') {
      input = <Segmented id={fieldId} value={value} onChange={(next) => setValue(field.name, next)} options={field.options} />;
    } else if (field.type === 'multi') {
      input = (
        <MultiChips
          id={fieldId}
          options={field.optionsFrom ? field.optionsFrom(values) : field.options}
          values={value}
          onChange={(next) => setValue(field.name, next)}
          customPlaceholder={`Autre ${field.label.toLowerCase().replace(/\(s\)/, '')}…`}
        />
      );
    } else {
      input = (
        <TextInput
          id={fieldId}
          type={field.type === 'date' ? 'date' : field.inputType || 'text'}
          step={field.step}
          value={value}
          onChange={(e) => setValue(field.name, e.target.value)}
          placeholder={field.placeholder}
          invalid={Boolean(errors[field.name])}
          required={field.required}
        />
      );
    }
    return (
      <Field
        key={field.name}
        id={fieldId}
        label={field.label}
        required={field.required}
        hint={field.hint}
        error={errors[field.name]}
        wide={field.wide || field.type === 'multi'}
      >
        {input}
      </Field>
    );
  };

  const renderSection = (section) => (
    <Panel key={section.id} title={section.title}>
      <div className="grid grid-cols-1 gap-[18px] sm:grid-cols-2">{section.fields.filter((field) => !field.recordOnly).map(renderField)}</div>
    </Panel>
  );

  return shell(
    <form onSubmit={submit} noValidate>
      <PageHeader
        back={back}
        eyebrow={editing ? 'Modifier le dossier' : 'Nouveau dossier'}
        title={editing ? patient.nom || 'Dossier patient' : 'Nouveau patient'}
        subtitle={editing ? 'Les champs laissés vides sont enregistrés vides.' : 'Seuls le numéro de dossier et le nom sont obligatoires.'}
      />

      <div className="mt-8 flex flex-col gap-[16px]">
        {!editing && (
          <Panel title="Unité">
            {myUnits.length > 1 ? (
              <Field id="field-unit" label="Unité qui suit le patient" hint="Seule cette unité pourra modifier le dossier.">
                <Select id="field-unit" value={unit} onChange={(e) => setUnit(e.target.value)} options={myUnits} placeholder="Mon unité (par défaut)" />
              </Field>
            ) : (
              <p className="m-0 text-[14px] text-black/55">
                Le dossier sera rattaché à votre unité{myUnits[0] ? ` : ${myUnits[0]}` : ''}. Seule cette unité pourra le modifier.
              </p>
            )}
          </Panel>
        )}

        {SECTIONS.filter((section) => !section.clinical).map(renderSection)}

        <button
          type="button"
          onClick={() => setClinicalOpen((open) => !open)}
          aria-expanded={clinicalOpen}
          className={`${CARD} flex w-full cursor-pointer items-center justify-between gap-[12px] border-0 px-6 py-[18px] text-left transition hover:shadow-[0_1px_2px_rgba(0,0,0,0.04),0_12px_28px_rgba(0,0,0,0.07)]`}
        >
          <span>
            <span className="block text-[16px] font-semibold tracking-tight text-ink">Dossier clinique</span>
            <span className="mt-0.5 block text-[13.5px] text-black/45">Clinique, examens, intervention, post-opératoire, suites et sortie</span>
          </span>
          <FiChevronDown size={20} aria-hidden="true" className={`flex-none text-black/40 transition-transform ${clinicalOpen ? 'rotate-180' : ''}`} />
        </button>
        {clinicalOpen && SECTIONS.filter((section) => section.clinical).map(renderSection)}

        {canAttach && (
          <Panel title="Documents" description="Facultatif : les documents à joindre dès la création. Vous pourrez en ajouter ensuite depuis le dossier.">
            <div className="grid grid-cols-1 gap-[18px]">
              <Field id="field-files-summary" label="Résumé" hint="Ce que contiennent ces documents (ex. bilan d’admission).">
                <TextInput
                  id="field-files-summary"
                  value={attached.ordre}
                  onChange={(e) => {
                    setDirty(true);
                    setAttached({ ...attached, ordre: e.target.value });
                  }}
                />
              </Field>
              <Field id="field-files" label="Documents">
                <AttachmentsField
                  id="field-files"
                  status={documentsReady}
                  links={attached.links}
                  onLinksChange={
                    saving
                      ? null
                      : (links) => {
                          setDirty(true);
                          setAttached((current) => ({ ...current, links }));
                        }
                  }
                  pending={attached.pending}
                  onPendingChange={(pending) => {
                    setDirty(true);
                    setAttached((current) => ({ ...current, pending }));
                  }}
                  sending={sending}
                />
              </Field>
            </div>
          </Panel>
        )}
      </div>

      {formError && (
        <div className="mt-6">
          <Notice tone="red" icon={FiAlertCircle}>
            {formError}
          </Notice>
        </div>
      )}

      <div className="sticky bottom-0 z-10 -mx-[20px] mt-8 border-0 border-t-[1px] border-solid border-black/[0.06] bg-mist/90 px-[20px] py-[14px] backdrop-blur-xl sm:-mx-8 sm:px-8 lg:-mx-10 lg:px-10">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
          {dirty && <span className="text-center text-[13px] text-black/45 sm:mr-auto sm:text-left">Modifications non enregistrées</span>}
          <button type="button" onClick={cancel} className={`${BUTTON.secondary} ${SIZE.md}`}>
            Annuler
          </button>
          <button type="submit" disabled={saving} className={`${BUTTON.primary} ${SIZE.md} min-w-[180px]`}>
            {sending
              ? `Envoi des documents ${sending.index + 1}/${attached.pending.length}…`
              : saving
                ? 'Enregistrement…'
                : editing
                  ? 'Enregistrer les modifications'
                  : 'Créer le dossier'}
          </button>
        </div>
      </div>
      {!editing && (
        <p className="mb-0 mt-[12px] text-center text-[12.5px] text-black/40">
          <Link to="/patients" className="text-black/50 underline hover:text-ink">
            Retour à la liste sans créer de dossier
          </Link>
        </p>
      )}
    </form>
  );
};

export default PatientForm;
